import { useCallback, useEffect, useReducer, useRef } from "react";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import LoadingSpinner from "../Loaders/LoadingSpinner";
import {
  UserIcon,
  ComputerDesktopIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import ResourceNotFound from "../NotFound/ResourceNotFound";
import NotFoundResourceIcon from "../NotFound/NotFoundResourceIcon";

import {
  fetchExecutionResultList,
} from "../../utils/api";
import { getLaunchDetailsSummary } from "../../utils/api/testlaunch_apis";
import { STATUS } from "../../utils/api/sharedfunction";
import TabView from "../tabView/TabView";
import ExecutionResultAutomatedTable from "./ExecutionResultAutomatedTable";
import ExecutionResultManualTable from "./ExecutionResultManualTable";
import LaunchDetailHeader from "./LaunchDetailHeader";
import { GroupCardRendering } from "./TestLaunchPrimaryReportsCard";
import TestLaunchExecutionDetails from "../TestLaunchExecutionDetails";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DEFAULT_ROUTES from "../../constants/routes";
import { setActivePage } from "../../features/navigation/navigationSlice";
import { buildTestStatusTooltip } from "./buildTestStatusTooltip";


const initialState = {
  rows: [],
  total: { automated: 0, manual: 0 },
  paginationModel: { page: 0, pageSize: 25 },
  activeTab: "automated", // values [automated,manual]
  summaryReport: null,
  isLoadingFetchTabularData: false,
  isLoadingSummaryData: false,
  notFound: false,
  executionDetailsData: {
    isModalOpen: false,
    data: null,
  },
  filterModel: {
    items: [],
    quickFilterValues: [],
  },
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_ROWS":
      return { ...state, rows: action.payload || [] };

    case "SET_TOTAL_ROWS_COUNT":
      return { ...state, total: { ...state.total, ...action.payload } };

    case "SET_PAGINATION_MODEL":
      return {
        ...state,
        paginationModel: { ...state.paginationModel, ...action.payload },
      };

    case "SET_ACTIVE_TAB":
      return { ...state, activeTab: action.payload || 0 };

    case "SET_SUMMARY_REPORT":
      return { ...state, summaryReport: action.payload, notFound: false };

    case "SET_LOADING_TABULAR":
      return { ...state, isLoadingFetchTabularData: action.payload };

    case "SET_LOADING_SUMMARY":
      return { ...state, isLoadingSummaryData: action.payload };

    case "SET_NOT_FOUND":
      return { ...state, notFound: action.payload };

    case "EXECUTION_DETAILS_DATA":
      return {
        ...state,
        executionDetailsData: {
          ...state.executionDetailsData,
          ...action.payload,
        },
      };

    case "SET_FILTERS":
      return {
        ...state,
        filterModel: {
          ...action.payload,
        },
      };

    case "RESET":
      return initialState;

    default:
      return state;
  }
}

const TestLaunchDetail = ({ isTestCase }) => {
  const navigate = useNavigate();
  const reduxDispatch = useDispatch();

  const { selectedProject } = useSelector((state) => state.projects);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { launch_id, group_id } = useParams();
  const activePage = useSelector((state) => state.navigation.activePage);
  const launchIdRef = useRef(launch_id);
  launchIdRef.current = launch_id;

  const fetchTabularData = useCallback(async (silent = false) => {
    if (!silent) {
      dispatch({ type: "SET_LOADING_TABULAR", payload: true }); // start loading
    }
    try {
      const addOnFilters = { executionMode: state.activeTab };
      const { data } = await fetchExecutionResultList({
        launch_id,
        ...state.paginationModel,
        page: state.paginationModel.page + 1,
        addOnFilters,
        filter: {
          filterField: state?.filterModel?.items[0]?.field,
          filterOperator: state?.filterModel?.items[0]?.operator,
          filterValue: state?.filterModel?.items[0]?.value,
        },
      });
      dispatch({ type: "SET_ROWS", payload: data?.rows });
      dispatch({
        type: "SET_TOTAL_ROWS_COUNT",
        payload: { automated: data?.automatedCount, manual: data?.manualCount },
      });
    } finally {
      if (!silent) {
        dispatch({ type: "SET_LOADING_TABULAR", payload: false }); // stop loading
      }
    }
  }, [launch_id, state.activeTab, state.paginationModel, state.filterModel]);

  const fetchLaunchDetailsSummaryReport = useCallback(async (silent = false) => {
    const requestLaunchId = launch_id;

    if (!silent) {
      dispatch({ type: "SET_NOT_FOUND", payload: false });
      dispatch({ type: "SET_SUMMARY_REPORT", payload: null });
      dispatch({ type: "SET_LOADING_SUMMARY", payload: true });
    }

    try {
      const response = await getLaunchDetailsSummary({ launchId: requestLaunchId });
      if (requestLaunchId !== launchIdRef.current) return;

      if (response.status === STATUS.SUCCESS) {
        dispatch({ type: "SET_SUMMARY_REPORT", payload: response.data });
      } else {
        dispatch({ type: "SET_NOT_FOUND", payload: true });
      }
    } finally {
      if (requestLaunchId === launchIdRef.current && !silent) {
        dispatch({ type: "SET_LOADING_SUMMARY", payload: false });
      }
    }
  }, [launch_id]);

  useEffect(() => {
    fetchLaunchDetailsSummaryReport();
  }, [fetchLaunchDetailsSummaryReport]);

  useEffect(() => {
    if (!state.notFound) {
      fetchTabularData();
    }
  }, [fetchTabularData, state.notFound]);

  const { summaryReport } = state;
  const {
    counts: {
      totalExecutionCount = 0,
      passedExecutionCount = 0,
      totalNLPCount = 0,
      passedNLPCount = 0,
      pendingNLPCount = 0,
      failedNLPCount = 0,
      skippedNLPCount = 0,
      abortedNLPCount = 0,
      inProgressNLPCount = 0,
      totalNonNLPCount = 0,
      passedNonNLPCount = 0,
      pendingNonNLPCount = 0,
      failedNonNLPCount = 0,
      skippedNonNLPCount = 0,
      inProgressNonNLPCount = 0,
    } = {},
  } = summaryReport || {};

  const onCLickViewDetails = (params) => {
    dispatch({
      type: "EXECUTION_DETAILS_DATA",
      payload: {
        isModalOpen: true,
        data: params.row,
      },
    });
  };
  const onCloseViewDetails = (params) => {
    dispatch({
      type: "EXECUTION_DETAILS_DATA",
      payload: initialState.executionDetailsData,
    });
  };
  const tabsData = [
    {
      name: `Automated Tests (${totalNLPCount})`,
      icon: <ComputerDesktopIcon className="icon" />,
      component: (
        <Box sx={{ bgcolor: "#f8fafc", pt: 4, pb: 0, px: 0 }}>
          <ExecutionResultAutomatedTable
            componentData={state}
            componentDataDispatch={dispatch}
            onCLickViewDetails={onCLickViewDetails}
            refetch={() => {
              fetchTabularData();
              fetchLaunchDetailsSummaryReport();
            }}
            silentRefetch={() => {
              fetchTabularData(true);
              fetchLaunchDetailsSummaryReport(true);
            }}
            projectId={selectedProject?.id}
          />
        </Box>
      ),
    },
    {
      name: `Manual Tests (${totalNonNLPCount})`,
      icon: <UserIcon className="icon" />,
      component: (
        <Box sx={{ bgcolor: "#f8fafc", pt: 4, pb: 0, px: 0 }}>
          <ExecutionResultManualTable
            componentData={state}
            componentDataDispatch={dispatch}
            refetch={() => {
              fetchTabularData();
              fetchLaunchDetailsSummaryReport();
            }}
            projectId={selectedProject?.id}
          />
        </Box>
      ),
    },
  ];
  const handleTabChange = (_, newValue) => {
    const tab = newValue == 1 ? "manual" : "automated";
    if (tab !== state.activeTab) {
      dispatch({
        type: "SET_PAGINATION_MODEL",
        payload: initialState.paginationModel,
      });
      dispatch({
        type: "SET_FILTERS",
        payload: initialState.filterModel,
      });
    }

    dispatch({
      type: "SET_ACTIVE_TAB",
      payload: tab,
    });
  };

  const totalPassPercentage = totalExecutionCount
    ? Number(((passedExecutionCount / totalExecutionCount) * 100).toFixed(2))
    : 0;

  const automatedTestStatusTooltip = buildTestStatusTooltip({
    total: totalNLPCount,
    label: 'automated',
    parts: [
      { key: 'passed', count: passedNLPCount },
      { key: 'failed', count: failedNLPCount },
      { key: 'skipped', count: skippedNLPCount },
      { key: 'aborted', count: abortedNLPCount },
      { key: 'pending', count: pendingNLPCount },
      { key: 'in progress', count: inProgressNLPCount },
    ],
  });

  const manualTestStatusTooltip = buildTestStatusTooltip({
    total: totalNonNLPCount,
    label: 'manual',
    parts: [
      { key: 'passed', count: passedNonNLPCount },
      { key: 'failed', count: failedNonNLPCount },
      { key: 'skipped', count: skippedNonNLPCount },
      { key: 'pending', count: pendingNonNLPCount },
      { key: 'in progress', count: inProgressNonNLPCount },
    ],
  });


  function handleBreadcrumbClick(id) {
    let rURL = "";
    let activePage = "";

    switch (id) {
      case "home":
        rURL = DEFAULT_ROUTES.BASE_URL;
        activePage = "Projects";
        break;
      case "project":
        rURL = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject?.id}/test-cases`;
        activePage = "Test Cases";
        break;
      case "test-group":
        rURL = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject?.id}/test-groups`;
        activePage = "Test Groups";
        break;
      
      case "test-group-id":
        rURL = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject?.id}/test-groups/${summaryReport?.testGroupId}`;
        activePage = "Test Groups";
        break;

      case "test-case":
        rURL = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject?.id}/test-cases`;
        activePage = "Test Cases";
        break;
      
      case "test-launch":
        rURL = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject?.id}/test-groups/${summaryReport?.testGroupId}?launch_details=true`;
        activePage = "Test Groups";
        break;

      default:
        return;
    }

    // Optionally dispatch activePage if needed
    if (activePage) {
      reduxDispatch(setActivePage(activePage));
      navigate(rURL);
    }
  }

  let breadcrumbItems = [
    {
      label: "Project",
      onClick: () => handleBreadcrumbClick("home"),
    },
    {
      label: selectedProject?.name || "Unknown Project",
    },
    {
      label: activePage,
      onClick: () => handleBreadcrumbClick("test-group"),
    },
    {
      label: summaryReport?.testGroupKey,
      onClick: () => handleBreadcrumbClick("test-group-id"),
    },
    {
      label: "Test Launches",
      onClick: () => handleBreadcrumbClick("test-launch"),
    },
    {
      label: summaryReport?.key,
    },
  ];

  if (isTestCase) {
    breadcrumbItems = [
      {
        label: "Project",
        onClick: () => handleBreadcrumbClick("home"),
      },
      {
        label: selectedProject?.name || "Unknown Project",
      },
      {
        label: activePage,
        onClick: () => handleBreadcrumbClick("test-case"),
      },
      {
        label: summaryReport?.key,
      },
    ];
  }

  if (state.notFound) {
    const handleLaunchNotFoundAction = () => {
      if (isTestCase) {
        reduxDispatch(setActivePage("Test Cases"));
        navigate(`${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject?.id}/test-cases`);
        return;
      }

      reduxDispatch(setActivePage("Test Groups"));
      if (group_id) {
        navigate(
          `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject?.id}/test-groups/${group_id}?launch_details=true`
        );
        return;
      }

      navigate(`${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject?.id}/test-groups`);
    };

    return (
      <ResourceNotFound
        icon={<NotFoundResourceIcon type="launch" />}
        title="Launch not found"
        message="This launch may have been deleted or you may not have access."
        actionLabel={isTestCase ? "Back to Test Cases" : "Go to all launches"}
        onAction={handleLaunchNotFoundAction}
      />
    );
  }

  if (state.isLoadingSummaryData) {
    return <LoadingSpinner message="Loading Launch Details..." />;
  }

  return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "background.paper", position:'relative' }}>

      {/* Header component */}
      <LaunchDetailHeader
        launch={summaryReport}
        breadcrumbItems={breadcrumbItems}
        refetch={() => {
          fetchTabularData();
          fetchLaunchDetailsSummaryReport();
        }}
      />

        {/*Reports card*/}
        <GroupCardRendering
          cards={[
            {
              title: "Overall Status",
              value: `${passedExecutionCount}/${totalExecutionCount}`,
              icon: <ChartBarIcon />,
              iconColor: "#2563EB",
              subText: `${totalPassPercentage}% Pass Rate`,
            },
            {
              title: "Automated Test Status",
              value: `${passedNLPCount}/${totalNLPCount}`,
              icon: <ComputerDesktopIcon />,
              iconColor: "#2563EB",
              subText: `${pendingNLPCount} Pending`,
              tooltip: automatedTestStatusTooltip,
            },
            {
              title: "Manual Test Status",
              value: `${passedNonNLPCount}/${totalNonNLPCount}`,
              icon: <UserIcon />,
              iconColor: "#16A34A",
              subText: `${pendingNonNLPCount} Pending`,
              tooltip: manualTestStatusTooltip,
            },
          ]}
          style={{ px: "25px" }}
        />

        {/*tabs component */}
        <Box sx={{ flex: 1, overflow: "auto", px: 3, pb: 3 }}>
          <TabView
            tabsData={tabsData}
            onTabChange={handleTabChange}
            parentActiveTab={state?.activeTab == "automated" ? 0 : 1}
          />
        </Box>

        {state.executionDetailsData?.data && (
          <TestLaunchExecutionDetails
            onClose={onCloseViewDetails}
            componentData={state.executionDetailsData}
            key={state.executionDetailsData?.data?.id}
          />
        )}
      </Box>
  );
};

export default TestLaunchDetail;
